import { useContext } from 'react';
import { CEPresentationContext, CEControls } from './CEPresentation';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import type { WorkshopCard, WorkshopStatus } from '../data/lacEpauleSeptembre2026Data';
import { prismeStoryScenes } from '../data/prismeImmersiveStory';
import PrismeStoryDiagrams from './PrismeStoryDiagrams';
import './PrismeImmersiveStory.css';

const sceneStorageKey = 'bz-lac-epaule-prisme-scene';
function initialScene() {
  try {
    const value = Number(window.localStorage.getItem(sceneStorageKey));
    return Number.isInteger(value) && value >= 0 && value < prismeStoryScenes.length ? value : 0;
  } catch { return 0; }
}

export default function PrismeImmersiveStory({ card, status, onClose, onStatusChange }: {
  card: WorkshopCard;
  status: WorkshopStatus;
  onClose: () => void;
  onStatusChange: (status: WorkshopStatus) => void;
}) {
  const ceContext = useContext(CEPresentationContext);
  const dialog = useRef<HTMLDialogElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [scene, setScene] = useState(initialScene);
  const [notes, setNotes] = useState(false);
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 700px)').matches);
  const active = prismeStoryScenes[scene];

  useEffect(() => {
    const node = dialog.current;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    node?.showModal();
    closeButton.current?.focus();
    document.body.style.overflow = 'hidden';
    const media = window.matchMedia('(max-width: 700px)');
    const change = () => setCompact(media.matches);
    media.addEventListener('change', change);
    return () => {
      media.removeEventListener('change', change);
      document.body.style.overflow = overflow;
      node?.close();
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    try { window.localStorage.setItem(sceneStorageKey, String(scene)); } catch { /* Private browsing still supports the presentation. */ }
    if (content.current) content.current.scrollTop = 0;
  }, [scene, notes]);

  const move = (step: number) => {
    setScene(current => Math.max(0, Math.min(prismeStoryScenes.length - 1, current + step)));
    setNotes(false);
  };

  return createPortal(
    <dialog ref={dialog} className="prisme-story" data-ce-presentation={ceContext ? "true" : undefined} aria-labelledby="prisme-story-title" onCancel={event => { event.preventDefault(); onClose(); }}
      onKeyDown={event => {
        if (event.key === 'Tab') {
          const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button, select, a[href], input, textarea, [tabindex="0"]') ?? []).filter(element => !element.hasAttribute('disabled') && element.getClientRects().length > 0);
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, [contenteditable=true]'))) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
      }}>
      <header className="prisme-story-topbar">
        <span className="prisme-story-wordmark">PRI<span>s</span>ME</span>
        <span className="prisme-story-event">Lac à l’épaule <span>· Septembre 2026</span></span>
        <button ref={closeButton} type="button" className="prisme-story-close" aria-label="Fermer la présentation PRIsME" onClick={onClose}><X size={22} /></button>
      </header>
      <div className="prisme-story-scroll" ref={content}>
        {notes ? <section className="prisme-story-notes" aria-label="Sources et préparation">
          <p className="prisme-story-eyebrow">Le contexte, sans encombrer la scène</p>
          <h2 id="prisme-story-title">Sources et préparation</h2>
          <h3>{active.label}</h3><p>{active.description}</p><p>{active.notes}</p>
          <h3>Document de préparation — {card.title}</h3>
          <p>Le contenu préparé est conservé ci-dessous. La présentation ne modifie ni la préparation ni l’état de discussion.</p>
          <p>{card.summary}</p><p>{card.whyNow}</p>
          {([
            ['Contexte', card.enrichedContext], ['Questions', card.questions], ['Décisions attendues', card.decisions],
            ['Signaux', card.signals], ['Références de préparation', card.evidence],
          ] as [string, string[]][]).map(([title, items]) => items.length > 0 && <section key={title}><h3>{title}</h3><ul>{items.map((item, i) => <li key={i}>{item}</li>)}</ul></section>)}
          {card.tabs?.map(tab => <section key={tab.id}><h3>{tab.title}</h3><ul>{tab.points.map((point, i) => <li key={i}>{point}</li>)}</ul></section>)}
          <label className="prisme-story-status">État de discussion
            <select value={status} onChange={event => onStatusChange(event.target.value as WorkshopStatus)}>
              <option value="a-discuter">À discuter</option><option value="en-discussion">En discussion</option><option value="complete">Complété</option>
            </select>
          </label>
        </section> : <article className={`prisme-story-scene prisme-story-scene-${active.id}`} key={active.id} data-scene={active.id}>
          <div className="prisme-story-heading"><p className="prisme-story-eyebrow">{active.eyebrow}</p><h2 id="prisme-story-title">{active.title}</h2></div>
          <PrismeStoryDiagrams scene={scene} compact={compact} description={active.description} />
          <div className="prisme-story-ending">
            {scene === 5 && <p className="prisme-story-foundations">{active.description}</p>}
            <p className="prisme-story-takeaway">{active.takeaway}</p>
            {scene === 5 && <p className="prisme-story-signature">Dany Bourassa <span>· Vision stratégique BZ</span></p>}
          </div>
        </article>}
      </div>
      <footer className="prisme-story-controls">
        <nav className="prisme-story-scenes" aria-label="Choisir une scène">
          {prismeStoryScenes.map((item, index) => <button type="button" key={item.id} aria-label={`Scène ${index + 1} : ${item.label}`} aria-current={scene === index ? 'step' : undefined} onClick={() => { setScene(index); setNotes(false); }}><span className="prisme-story-scene-number">{index + 1}</span><span className="prisme-story-scene-name">{item.label}</span></button>)}
        </nav>
        <div className="prisme-story-actions">
          <button type="button" className="prisme-story-details" aria-expanded={notes} onClick={() => setNotes(value => !value)}>{notes ? 'Retour à la scène' : 'Sources et préparation'}</button>
          <span className="prisme-story-counter" aria-live="polite" aria-atomic="true">{scene + 1} / {prismeStoryScenes.length}<span className="sr-only"> · {active.label}</span></span>
          <button type="button" aria-label="Scène précédente" aria-disabled={scene === 0} onClick={() => move(-1)}><ArrowLeft size={22} /></button>
          <button type="button" aria-label="Scène suivante" aria-disabled={scene === prismeStoryScenes.length - 1} onClick={() => move(1)}><ArrowRight size={22} /></button>
        </div>
      </footer>
    <CEControls/></dialog>, document.body,
  );
}
